package com.qrkontaq

import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.key
import com.qrkontaq.di.ProvideAppContainer
import com.qrkontaq.ui.components.ConsentBanner
import com.qrkontaq.ui.localization.ProvideLanguage
import com.qrkontaq.ui.localization.rememberLanguage
import com.qrkontaq.ui.theme.AppColors
import com.qrkontaq.ui.theme.initAppTheme
import com.qrkontaq.utils.OAuthHelper
import com.qrkontaq.utils.Storage
import com.qrkontaq.utils.StorageKeys
import com.varabyte.kobweb.compose.css.ScrollBehavior
import com.varabyte.kobweb.compose.ui.modifiers.*
import com.varabyte.kobweb.core.App
import com.varabyte.kobweb.silk.SilkApp
import com.varabyte.kobweb.silk.components.layout.Surface
import com.varabyte.kobweb.silk.init.InitSilk
import com.varabyte.kobweb.silk.init.InitSilkContext
import com.varabyte.kobweb.silk.style.common.SmoothColorStyle
import com.varabyte.kobweb.silk.style.toModifier
import com.varabyte.kobweb.silk.style.breakpoint.BreakpointSizes
import com.varabyte.kobweb.silk.theme.colors.ColorMode
import org.jetbrains.compose.web.css.px
import org.jetbrains.compose.web.css.vh

@InitSilk
fun initSilk(ctx: InitSilkContext) {
    // Hide the static snapshot only for the teardown/re-render gap: this hook
    // runs inside app.js right before Compose wipes the prerendered DOM, and
    // AppEntry's LaunchedEffect lifts the gate once Compose owns it. Until app.js
    // gets here the visitor sees the prerendered page, not a blank screen (the
    // gate CSS is inlined in <head> by build.gradle.kts). The timeout is a
    // safety net: if the first composition dies, show the snapshot again.
    kotlinx.browser.document.documentElement?.classList?.add("qk-boot")
    kotlinx.browser.window.setTimeout({
        kotlinx.browser.document.documentElement?.classList?.remove("qk-boot")
    }, 3000)

    val savedTheme = try {
        Storage.get(StorageKeys.THEME)
    } catch (_: Exception) { null }
    ctx.config.initialColorMode = when (savedTheme) {
        "LIGHT" -> ColorMode.LIGHT
        "DARK" -> ColorMode.DARK
        else -> ColorMode.DARK
    }

    ctx.theme.breakpoints = BreakpointSizes(
        sm = 0.px,
        md = 768.px,
        lg = 1280.px,
        xl = 1920.px
    )

    initAppTheme(ctx)
}

@App
@Composable
fun AppEntry(content: @Composable () -> Unit) {
    LaunchedEffect(Unit) {
        // Compose owns the DOM now — lift the boot gates (see the inline
        // head scripts in build.gradle.kts) so the real content becomes visible.
        kotlinx.browser.document.documentElement?.classList?.remove("qk-tenant-boot")
        kotlinx.browser.document.documentElement?.classList?.remove("qk-lang-boot")
        kotlinx.browser.document.documentElement?.classList?.remove("qk-boot")
        OAuthHelper.loadGoogleSDK {
            console.log("Google SDK loaded successfully")
        }
        installPwaHead()
        com.qrkontaq.utils.SeoHelper.dedupeHeadTags()
        com.qrkontaq.core.analytics.Analytics.init()
    }

    ProvideAppContainer {

        val languageState = rememberLanguage()

        key("silk-app") {
            SilkApp {
                ProvideLanguage(languageState) {
                    val colorMode = ColorMode.current
                    val colors = AppColors.getColors(colorMode)

                    Surface(
                        SmoothColorStyle.toModifier()
                            .minHeight(100.vh)
                            .scrollBehavior(ScrollBehavior.Smooth)
                            .backgroundColor(colors.background)
                            .color(colors.textPrimary)
                    ) {
                        content()
                        // Shows itself only on the marketing host (where the
                        // gtag head script is active) and until a choice is made.
                        ConsentBanner()
                    }
                }
            }
        }
    }
}

/** Adds the PWA manifest + Apple meta so the admin can be installed to the home screen. */
private fun installPwaHead() {
    val head = kotlinx.browser.document.head ?: return
    if (kotlinx.browser.document.getElementById("qk-pwa-manifest") != null) return

    fun link(id: String, rel: String, href: String) {
        val el = kotlinx.browser.document.createElement("link")
        el.id = id
        el.setAttribute("rel", rel)
        el.setAttribute("href", href)
        head.appendChild(el)
    }
    fun meta(name: String, content: String) {
        val el = kotlinx.browser.document.createElement("meta")
        el.setAttribute("name", name)
        el.setAttribute("content", content)
        head.appendChild(el)
    }
    link("qk-pwa-manifest", "manifest", "/manifest.webmanifest")
    link("qk-apple-icon", "apple-touch-icon", "/icons/icon-192.png")
    meta("apple-mobile-web-app-capable", "yes")
    meta("mobile-web-app-capable", "yes")
    meta("apple-mobile-web-app-status-bar-style", "black-translucent")
    meta("theme-color", "#0F9D6E")
}
